import { AccountWrapper } from '@components/account/AccountWrapper'
import { useRouter } from 'next/router'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import useCustomer from '@helpers/hooks/customer/useCustomer'
import AccountOrders from './orders'
import AccountMyFit from './my-fit'
import AccountDetails from './details'
import PageError from '@components/PageError'
import AccountOverview from './overview'
import { useEffect } from 'react'
import { getHelpdeskLink, getLink } from '@helpers/env'
import { ACCOUNT_PAGE_NAME } from '@api/accounts/auth/consts'
import ErrorBoundary from '@components/ErrorBoundary'
import Translations from '@helpers/translations'
import useIsReactClient from '@helpers/hooks/useIsReactClient'
import OrderPage from './orders/OrderPage'

const mapToContent = {
  account: AccountOverview,
  overview: AccountOverview,
  orders: AccountOrders,
  'orders-page': OrderPage,
  'account-details': AccountDetails,
  'your-fit': AccountMyFit
}

function AccountPage() {
  const isReactClient = useIsReactClient()
  const router = useRouter()
  const { handleCallback, userProfile, token } = useAuth()
  useCustomer(userProfile)

  const query = router.query
  const pageHande = (query?.page as string) || 'account'
  const hasOAuthParams =
    typeof query?.code === 'string' && typeof query?.state === 'string'
  const isValidPage =
    Object.keys(mapToContent).includes(pageHande) ||
    hasOAuthParams ||
    router?.asPath?.includes('/account')

  useEffect(() => {
    if (!router.isReady) {
      return
    }
    const { code, state } = query

    if (!token && typeof code === 'string' && typeof state === 'string') {
      handleCallback(code, state).then(() => {
        // Clean up the URL to remove the code and state query params
        const overviewUrl = getLink(`/${ACCOUNT_PAGE_NAME}?page=overview`)
        router.replace(overviewUrl, undefined, {
          shallow: true
        })
      })
    }
  }, [router.isReady, query, handleCallback, router, token])

  if (!isValidPage && isReactClient) {
    return (
      <ErrorBoundary>
        <div>
          <PageError title="Oops, something's gone wrong.">
            <p>{Translations.localise('common.layout.error.line1')}</p>
            <p>
              {Translations.localise('common.layout.error.line2')}{' '}
              <a
                href={getHelpdeskLink()}
                target="_blank"
                rel="noopener noreferrer"
              >
                FAQs
              </a>{' '}
              {Translations.localise('common.layout.error.line3')}
            </p>
          </PageError>
        </div>
      </ErrorBoundary>
    )
  }
  const Content =
    mapToContent[pageHande?.toLowerCase() as keyof typeof mapToContent] ||
    mapToContent['account' as keyof typeof mapToContent]

  const pagesWithoutSidebar = ['account', 'overview']
  return (
    <AccountWrapper withSidebar={!pagesWithoutSidebar.includes(pageHande)}>
      <Content />
    </AccountWrapper>
  )
}
export default AccountPage
